import { Box, DialogContent, Stack } from "@mui/material";
import { USA_STATES } from "@src/appV2/lib/utils";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import {
  BottomSheet,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { filter } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DialogFooter } from "../../components/DialogFooter";
import { type AddLicenseFormData } from "./FormProvider";

interface AddLicenseAddStateBottomSheetProps {
  modalState: UseModalState;
}

export function AddLicenseAddStateBottomSheet(props: AddLicenseAddStateBottomSheetProps) {
  const { modalState } = props;

  const [searchTerm, setSearchTerm] = useState("");

  const { setValue } = useFormContext<AddLicenseFormData>();

  const debouncedSearchQuery = useDebounce({ value: searchTerm, debounceTimeInMs: 300 });

  const filteredStates = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return USA_STATES;
    }

    return filter(USA_STATES, (state) => {
      const stateSearchString = `${state.name.toLowerCase()} ${state.key.toLowerCase()}`;
      return stateSearchString.includes(debouncedSearchQuery.toLowerCase());
    });
  }, [debouncedSearchQuery]);

  const handleStateSelect = (stateName: string) => {
    setValue("state", stateName);
    modalState.closeModal();
  };

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      footer={<DialogFooter orientation="vertical" />}
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <DialogContent sx={{ padding: 5, paddingTop: 8, height: "100%" }}>
        <Stack spacing={3}>
          <Stack spacing={7}>
            <Text semibold variant="h4">
              Enter state
            </Text>
            <Input
              size="medium"
              placeholder="State"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
            />
          </Stack>
          <Box sx={{ paddingX: 3 }}>
            <List dense>
              {filteredStates.map((state) => (
                <ListItem key={state.key}>
                  <ListItemButton
                    onClick={() => {
                      handleStateSelect(state.name);
                    }}
                  >
                    <ListItemText>{state.name}</ListItemText>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            {filteredStates.length === 0 && (
              <Text
                variant="body1"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  paddingY: 10,
                  textAlign: "center",
                })}
              >
                No states matching your search
              </Text>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
